import { useCallback, useEffect } from 'react';
import { captureException } from '@sentry/nextjs';
import { capitalize } from 'lodash';
import { useRouter } from 'next/router';
import toast from 'react-hot-toast';
import { REVIEW_COACH_QUERY_PARAM } from 'constants/pages';
import { EMPTY_COACH_AVATAR_SRC } from 'constants/user';
import {
  CoachExternalReviewSourcesEnum,
  CoachReviewTypesEnum,
  useGetCoachReviewCheckLazyQuery,
  useInsertUserReviewMutation,
} from 'types/generated/client';
import { useViewer } from 'hooks/useViewer';
import CloseIcon from 'svg/CloseIcon';
import { ButtonText } from 'components/Button';
import ReviewForm from 'components/ReviewForm';
import type { ReviewFormValues } from 'components/ReviewForm';
import Modal, { useModal } from 'components/modals/Modal';

const ModalSubmitReview: React.FC = () => {
  const { isOpen, closeModal, openModal } = useModal();
  const [insertUserReview, { loading }] = useInsertUserReviewMutation();

  const [getCoachReviewCheck, { data: CoachReviewCheckData }] = useGetCoachReviewCheckLazyQuery();

  const viewer = useViewer();

  const router = useRouter();

  const coachData = CoachReviewCheckData?.coach?.[0];

  const resetQuery = useCallback(() => {
    router.replace(router.asPath.split('?')[0], undefined, { shallow: true });
  }, [router]);

  const closeAndResetQuery = () => {
    closeModal();
    resetQuery();
  };

  const onSubmit = async (data: ReviewFormValues) => {
    try {
      const { lessonId, videoAnalysisId, coachId } = router.query;
      if (!lessonId && !videoAnalysisId) return;
      await insertUserReview({
        variables: {
          lessonId: lessonId || undefined,
          rating: data.rating,
          review: data.review,
          coachUserId: coachId,
          reviewerUserId: viewer.userId,
          videoAnalysisId: videoAnalysisId || undefined,
          type: lessonId ? CoachReviewTypesEnum.Court : CoachReviewTypesEnum.Remote,
          externalSource: CoachExternalReviewSourcesEnum.Other,
        },
      });
      closeAndResetQuery();
    } catch (error) {
      console.error('Error submitting review:', error);
    }
  };

  useEffect(() => {
    const { lessonId, videoAnalysisId, coachId, playerId } = router.query;
    const init = async () => {
      try {
        const payload = {
          coachUserId: coachId,
          playerUserId: viewer.userId,
          ...(lessonId && { lessonId }),
          ...(videoAnalysisId && { videoAnalysisId }),
        };
        const coachReviewCheckRes = await getCoachReviewCheck({
          variables: payload,
          nextFetchPolicy: 'network-only',
          fetchPolicy: 'network-only',
        });

        if (coachReviewCheckRes.data?.reviews.length === 0) {
          openModal();
          return;
        } else if (coachReviewCheckRes.data!?.reviews.length > 0) {
          const coachName = coachReviewCheckRes.data?.coach?.[0]?.fullName;
          toast.success(`You have already reviewed Coach ${coachName}`);
        }
        resetQuery();
      } catch (err) {
        captureException(err);
      }
    };
    if (
      !viewer.userId ||
      typeof playerId !== 'string' ||
      typeof router.query[REVIEW_COACH_QUERY_PARAM] !== 'string'
    ) {
      return;
    }
    if (viewer.userId !== playerId || router.query[REVIEW_COACH_QUERY_PARAM] !== 'true') {
      resetQuery();
      return;
    }

    init();
  }, [getCoachReviewCheck, viewer.userId, router.query, resetQuery]);

  return (
    <Modal isOpen={isOpen} handleClose={closeAndResetQuery} classNameMaxWidth="max-w-[40rem]">
      <div className="flex flex-row-reverse px-ds-2xl py-ds-xl">
        <ButtonText onClick={() => closeAndResetQuery()}>
          <CloseIcon className="h-4 w-4" />
        </ButtonText>
      </div>
      <ReviewForm
        media={
          <img
            className="relative h-36 w-36 rounded-full object-cover object-top"
            src={coachData?.profileImageProviderUrl || EMPTY_COACH_AVATAR_SRC}
            alt=""
          />
        }
        heading={`How was your lesson with ${capitalize(coachData?.fullName || '')}?`}
        placeholder="Say a few words about the coach and lesson."
        isSubmitting={loading}
        onSubmit={onSubmit}
      />
    </Modal>
  );
};

export default ModalSubmitReview;
